import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Building2 } from 'lucide-react';
import { Botao } from '../../componentes/base';
import { Formulario, type DefCampo } from '../../componentes/formulario';
import { CampoVerticais, verticaisDaCriacao, type ValorVerticais } from '../../componentes/verticais';
import { get, patch, post } from '../../nucleo/api';
import { comMaisOpcoes } from '../../nucleo/modo-simples';
import { useSessao } from '../../nucleo/sessao';
import { NovoLead } from './NovoLead';
import { opcoesRegime, sugestaoDeRegime } from './crm-regime';
import type { Equipe, Oportunidade } from './tipos';

export function useEquipes() {
  return useQuery({ queryKey: ['pipeline', 'equipes'], queryFn: () => get<{ itens: Equipe[] }>('/pipeline/equipes') });
}

/** Corpo enviado à API a partir dos valores do formulário (texto vazio vira nulo, como a API espera). */
export function corpoDaOportunidade(v: Record<string, unknown>, valorPelosItens = false, criando = false) {
  const txt = (x: unknown) => (typeof x === 'string' && x.trim() ? x.trim() : null);
  return {
    titulo: v.titulo,
    parceiro_id: v.parceiro_id,
    // Review r46 spec (B1): com itens, o valor é a soma deles (o servidor recusa outro); o formulário não o manda.
    valor_centavos: valorPelosItens ? undefined : ((v.valor_centavos as number | null | undefined) ?? 0),
    responsavel_id: txt(v.responsavel_id),
    equipe_id: txt(v.equipe_id),
    indicado_por_id: txt(v.indicado_por_id),
    origem: txt(v.origem) ?? 'manual',
    canal: txt(v.canal),
    utm_fonte: txt(v.utm_fonte),
    utm_midia: txt(v.utm_midia),
    utm_campanha: txt(v.utm_campanha),
    previsao_fechamento: txt(v.previsao_fechamento),
    // Lote crm5 (E10): na criação, vazio não vai (o servidor sugere o regime em vigor da empresa); na edição, vazio limpa.
    regime_pretendido: txt(v.regime_pretendido) ?? (criando ? undefined : null),
    extras: v.extras ?? {},
  };
}

/** Formulário de criação e edição da oportunidade (M2.1). O botão de enviar fica no rodapé do Drawer (form="form-oportunidade"). */
export function FormOportunidade({
  atual,
  usuarioId,
  funilId,
  aoSalvar,
  somenteLeitura,
  parceiroInicial,
}: {
  atual?: Oportunidade;
  usuarioId?: string;
  /** U13: funil em que a oportunidade nova entra (o escolhido no quadro). */
  funilId?: string;
  aoSalvar: (r: { id?: string }) => Promise<void> | void;
  somenteLeitura?: boolean;
  /** CRM lote 2: cliente já escolhido (a negociação criada a partir da tarefa, com a empresa dela). */
  parceiroInicial?: { id: string; nome: string };
}) {
  const { t } = useTranslation('pipeline');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const equipes = useEquipes();
  const valorPelosItens = !!atual?.itens?.length;
  const qc = useQueryClient();
  // Item 12 (21/10): lead criado na hora pelo CNPJ; vira o cliente escolhido e dá o título, se ainda vazio.
  const [rapido, setRapido] = useState(false);
  const [lead, setLead] = useState<{ id: string; nome: string; n: number } | null>(null);
  // Ficha 2026-10-22: as verticais já na criação (null = o escopo de quem cria, como antes).
  const [verticais, setVerticais] = useState<ValorVerticais>(null);
  const podeLead = !somenteLeitura && !atual && s.pode('cadastros.criar');
  const campos: DefCampo[] = [
    // r10 visual M2: o título costuma citar o cliente; com a opção de nomes, sem foco ele aparece com as iniciais.
    { nome: 'titulo', rotulo: t('campos.titulo'), tipo: 'texto', obrigatorio: true, desabilitado: somenteLeitura, nomes: [atual?.parceiro_nome] },
    {
      nome: 'parceiro_id',
      rotulo: t('campos.parceiro'),
      tipo: 'busca',
      obrigatorio: true,
      largura: 'meia',
      desabilitado: somenteLeitura,
      busca: { caminho: '/cadastros/parceiros', rotulo: (i) => String(i.nome) },
      // Lote crm5 (E10): na criação, a empresa escolhida sugere o regime pretendido (só se o campo estiver vazio).
      // RCRM5-SEG-M1: só para quem lê o Cadastros; SPEC-B3: a sugestão segue a empresa escolhida (e a que já vem na abertura).
      ...(atual || !s.pode('cadastros.ver') ? {} : { preencher: sugestaoDeRegime, preencherSugere: true }),
    },
    {
      nome: 'valor_centavos',
      rotulo: t('campos.valor'),
      tipo: 'dinheiro',
      largura: 'meia',
      desabilitado: somenteLeitura || valorPelosItens,
      ajuda: valorPelosItens ? t('itens.valorPelosItens') : undefined,
    },
    { nome: 'responsavel_id', rotulo: t('campos.responsavel'), tipo: 'busca', largura: 'meia', desabilitado: somenteLeitura, busca: { caminho: '/membros', rotulo: (i) => String(i.nome) } },
    {
      nome: 'equipe_id',
      rotulo: t('campos.equipe'),
      tipo: 'selecao',
      largura: 'meia',
      desabilitado: somenteLeitura,
      opcoes: (equipes.data?.itens ?? []).map((e) => ({ valor: e.id, rotulo: e.nome })),
    },
    { nome: 'previsao_fechamento', rotulo: t('campos.previsao'), tipo: 'data', largura: 'meia', desabilitado: somenteLeitura },
    { nome: 'regime_pretendido', rotulo: t('campos.regimePretendido'), tipo: 'selecao', largura: 'meia', desabilitado: somenteLeitura, opcoes: opcoesRegime(), ajuda: atual ? undefined : t('campos.regimePretendidoAjuda') },
    // U5: quem indicou (vínculo com o parceiro; base do painel do parceiro).
    { nome: 'indicado_por_id', rotulo: t('campos.indicadoPor'), tipo: 'busca', largura: 'meia', desabilitado: somenteLeitura, busca: { caminho: '/cadastros/parceiros', rotulo: (i) => String(i.nome) } },
    { nome: 'origem', rotulo: t('campos.origem'), tipo: 'texto', largura: 'meia', desabilitado: somenteLeitura },
    { nome: 'canal', rotulo: t('campos.canal'), tipo: 'texto', largura: 'meia', desabilitado: somenteLeitura },
    { nome: 'utm_fonte', rotulo: t('campos.utmFonte'), tipo: 'texto', largura: 'terco', desabilitado: somenteLeitura },
    { nome: 'utm_midia', rotulo: t('campos.utmMidia'), tipo: 'texto', largura: 'terco', desabilitado: somenteLeitura },
    { nome: 'utm_campanha', rotulo: t('campos.utmCampanha'), tipo: 'texto', largura: 'terco', desabilitado: somenteLeitura },
  ];
  const inicial = atual ? ({ ...atual } as Record<string, unknown>) : { responsavel_id: usuarioId ?? null, origem: 'manual', ...(parceiroInicial ? { parceiro_id: parceiroInicial.id, titulo: parceiroInicial.nome } : {}) };
  // U15 F5: no modo simples, responsável (já nasce a própria pessoa), equipe, indicação, origem, canal e UTM ficam em "Mais
  // opções" (nenhum é obrigatório; a origem já nasce "manual"). Só na criação: ao editar, a pessoa vê tudo.
  const maisOpcoes = comMaisOpcoes(campos, {
    ativo: s.modoSimples && !atual,
    avancados: ['responsavel_id', 'equipe_id', 'regime_pretendido', 'indicado_por_id', 'origem', 'canal', 'utm_fonte', 'utm_midia', 'utm_campanha'],
    rotulo: tn('comum.maisOpcoes'),
    inicial,
  });
  return (
    <>
      {podeLead && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-borda bg-sup-1 px-3 py-2">
          <p className="text-sm text-tinta-2">{t('leadRapido.chamada')}</p>
          <Botao icone={<Building2 className="size-4" />} onClick={() => setRapido(true)}>
            {t('leadRapido.titulo')}
          </Botao>
        </div>
      )}
      <NovoLead
        aberto={rapido}
        aoFechar={() => setRapido(false)}
        aoCriar={(p) => {
          setLead((x) => ({ ...p, n: (x?.n ?? 0) + 1 }));
          void qc.invalidateQueries({ queryKey: ['busca', '/cadastros/parceiros'] });
        }}
      />
    <Formulario
      key={atual?.id ?? 'novo'}
      id="form-oportunidade"
      campos={maisOpcoes.campos}
      gruposRecolhiveis={maisOpcoes.gruposRecolhiveis}
      entidade="pipeline.oportunidade"
      inicial={inicial}
      // Review r47 visual (M1): com itens, o campo travado mostra a soma atual (salvar os itens no mesmo drawer muda o valor).
      sobrepor={valorPelosItens ? { valor_centavos: atual!.valor_centavos } : lead ? { parceiro_id: lead.id } : undefined}
      ajuste={lead ? { n: lead.n, dados: { titulo: lead.nome } } : undefined}
      rotulosAtuais={
        atual ? { parceiro_id: atual.parceiro_nome ?? '', responsavel_id: atual.responsavel_nome ?? '', indicado_por_id: atual.indicado_por_nome ?? '' } : lead ? { parceiro_id: lead.nome } : parceiroInicial ? { parceiro_id: parceiroInicial.nome } : undefined
      }
      aoEnviar={async (v) => {
        if (somenteLeitura) return;
        if (atual) {
          await patch(`/pipeline/oportunidades/${atual.id}`, corpoDaOportunidade(v, valorPelosItens));
          await aoSalvar({});
        } else {
          const r = await post<{ id: string }>('/pipeline/oportunidades', { ...corpoDaOportunidade(v, false, true), ...(funilId ? { funil_id: funilId } : {}), ...verticaisDaCriacao(verticais) });
          await aoSalvar(r);
        }
      }}
    >
      {!atual && <CampoVerticais valor={verticais} aoMudar={setVerticais} />}
    </Formulario>
    </>
  );
}
